/* Moteur d'Origination — Annuaire → Matrice d'influence.
 *
 * 3 vues :
 *  1. Écosystèmes présumés — client → projection IA de son écosystème → cibles
 *  2. Chemins d'introduction — cible | introduit par | priorité | → Lead
 *  3. Matrice d'influence — clients × cibles avec score d'origination multi-canal
 * + Scan frictions (signaux internes + web) par acteur.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import AiText from "@/components/AiText";
import { SplcButton } from "@/components/AnnuaireSPLC";
import {
  Loader2, Sparkles, Network, ArrowRight, Grid3X3, Route, Crown,
  RefreshCw, AlertTriangle, Radar, CheckCircle2, Landmark, Building2,
  User, Scale, ShieldCheck, Coins, Users2, Globe2, HelpCircle,
} from "lucide-react";

const ROLE_META = {
  banquier: { label: "Banquier", icon: Landmark, color: "#378ADD" },
  fiduciaire: { label: "Fiduciaire", icon: Building2, color: "#1D9E75" },
  avocat_notaire: { label: "Avocat / Notaire", icon: Scale, color: "#7F77DD" },
  assureur: { label: "Assureur", icon: ShieldCheck, color: "#EF9F27" },
  fournisseur_cle: { label: "Fournisseur clé", icon: Building2, color: "#E24B4A" },
  client_du_client: { label: "Client du client", icon: Users2, color: "#378ADD" },
  partenaire: { label: "Partenaire", icon: Users2, color: "#1D9E75" },
  investisseur: { label: "Investisseur", icon: Coins, color: "#EF9F27" },
  regulateur: { label: "Régulateur", icon: Globe2, color: "#888" },
  autre: { label: "Autre", icon: HelpCircle, color: "#888" },
};

const PRIORITY_STYLE = {
  haute: "bg-[#1D9E75]/15 text-[#1D9E75] border-[#1D9E75]/30",
  moyenne: "bg-[#EF9F27]/15 text-[#EF9F27] border-[#EF9F27]/30",
  basse: "bg-white/5 text-zinc-400 border-white/10",
};

function ScoreBadge({ score, priority }) {
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${PRIORITY_STYLE[priority] || PRIORITY_STYLE.basse}`}>
      {Math.round(score)}/100
    </span>
  );
}

function RolePill({ role }) {
  const meta = ROLE_META[role] || ROLE_META.autre;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-white/10" style={{ color: meta.color }}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function OriginationEngine() {
  const [tab, setTab] = useState("ecosystems"); // ecosystems | paths | matrix
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    try {
      const r = await api.get("/workspace/annuaire/origination/overview");
      setOverview(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const stats = overview?.stats || {};

  return (
    <div className="space-y-4" data-testid="origination-engine-root">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Clients / relais" value={stats.clients_total ?? "—"} icon={Crown} />
        <StatCard label="Écosystèmes projetés" value={stats.ecosystems_done ?? "—"} icon={Network} />
        <StatCard label="Cibles identifiées" value={stats.targets_total ?? "—"} icon={Radar} />
        <StatCard label="Cibles multi-canaux" value={stats.multi_channel_targets ?? "—"} icon={Grid3X3} accent />
      </div>

      <div className="inline-flex rounded-md border border-white/10 bg-black/30 p-0.5" data-testid="origination-tabs">
        {[
          { key: "ecosystems", label: "Écosystèmes présumés", icon: Network },
          { key: "paths", label: "Chemins d'introduction", icon: Route },
          { key: "matrix", label: "Matrice d'influence", icon: Grid3X3 },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`text-[11px] px-3 py-1.5 rounded inline-flex items-center gap-1.5 transition-colors ${
              tab === key ? "bg-[#378ADD]/20 text-[#378ADD]" : "text-zinc-400 hover:text-white"
            }`}
            data-testid={`origination-tab-${key}`}
          >
            <Icon className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : tab === "ecosystems" ? (
        <EcosystemsTab overview={overview} onChanged={loadOverview} />
      ) : tab === "paths" ? (
        <PathsTab />
      ) : (
        <MatrixTab />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`noam-card p-3 ${accent ? "border-[#EF9F27]/30" : ""}`}>
      <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase tracking-wider">
        <Icon className={`w-3 h-3 ${accent ? "text-[#EF9F27]" : "text-[#378ADD]"}`} /> {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

/* ===== Vue 1 — Écosystèmes présumés ===== */

function EcosystemsTab({ overview, onChanged }) {
  const entities = overview?.entities || [];
  const clients = entities.filter((e) => e.is_client);
  const others = entities.filter((e) => !e.is_client);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!selectedId && clients.length) {
      const withEco = clients.find((c) => c.eco_status === "done");
      setSelectedId((withEco || clients[0]).id);
    }
  }, [clients, selectedId]);

  const selected = entities.find((e) => e.id === selectedId);

  if (!entities.length) {
    return (
      <div className="noam-card p-8 text-center text-zinc-400 text-sm" data-testid="origination-empty">
        Annuaire vide — créez d'abord vos clients dans l'Annuaire pour projeter leurs écosystèmes.
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 noam-card p-3 max-h-[720px] overflow-y-auto" data-testid="origination-clients-list">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-1 mb-2">
          Clients & relais ({clients.length})
        </div>
        {clients.map((c) => (
          <ClientRow key={c.id} c={c} selected={selectedId === c.id} onSelect={() => setSelectedId(c.id)} />
        ))}
        {others.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-1 mt-4 mb-2">
              Autres entités ({others.length})
            </div>
            {others.map((c) => (
              <ClientRow key={c.id} c={c} selected={selectedId === c.id} onSelect={() => setSelectedId(c.id)} />
            ))}
          </>
        )}
      </div>
      <div className="lg:col-span-3">
        {selected ? (
          <EcosystemDetail key={selected.id} entity={selected} onChanged={onChanged} />
        ) : (
          <div className="noam-card p-8 text-center text-zinc-500 text-sm">Sélectionnez un client.</div>
        )}
      </div>
    </div>
  );
}

function ClientRow({ c, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-2.5 py-2 rounded-md mb-1 border transition-colors ${
        selected ? "border-[#378ADD]/40 bg-[#378ADD]/10" : "border-transparent hover:bg-white/5"
      }`}
      data-testid={`origination-client-${c.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate">{c.name}</span>
        {c.eco_status === "done" ? (
          <span className="text-[10px] text-[#1D9E75] inline-flex items-center gap-1 shrink-0">
            <CheckCircle2 className="w-3 h-3" /> {c.eco_nodes_count} cibles
          </span>
        ) : c.eco_status === "generating" ? (
          <Loader2 className="w-3 h-3 animate-spin text-[#378ADD] shrink-0" />
        ) : (
          <span className="text-[10px] text-zinc-500 shrink-0">Non projeté</span>
        )}
      </div>
      <div className="text-[11px] text-zinc-500 truncate">{c.sector || c.type} · {c.status}</div>
    </button>
  );
}

function EcosystemDetail({ entity, onChanged }) {
  const [eco, setEco] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchEco = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/annuaire/origination/ecosystem/${entity.id}`);
      setEco(r.data);
      if (r.data.status !== "generating" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        onChanged();
      }
    } catch (e) { /* poll silencieux */ }
  }, [entity.id, onChanged]);

  useEffect(() => {
    fetchEco();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchEco]);

  useEffect(() => {
    if (eco?.status === "generating" && !pollRef.current) {
      pollRef.current = setInterval(fetchEco, 3000);
    }
  }, [eco?.status, fetchEco]);

  const project = async () => {
    setBusy(true);
    try {
      await api.post(`/workspace/annuaire/origination/project/${entity.id}`);
      setEco((prev) => ({ ...(prev || {}), status: "generating" }));
      if (!pollRef.current) pollRef.current = setInterval(fetchEco, 3000);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const nodes = eco?.nodes || [];
  const generating = eco?.status === "generating";

  return (
    <div className="space-y-4">
      <div className="noam-card p-4" data-testid="origination-eco-detail">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold">{entity.name}</div>
            <div className="text-[11px] text-zinc-500">
              {entity.sector || entity.type} · {entity.country || "—"}
              {eco?.generated_at && <> · Projeté le {new Date(eco.generated_at).toLocaleDateString("fr-CH")}</>}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={project}
              disabled={busy || generating}
              className="btn-primary text-xs"
              data-testid="origination-project-btn"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : eco?.status === "done" ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
              {generating ? "Projection en cours…" : eco?.status === "done" ? "Re-projeter" : "Projeter l'écosystème"}
            </button>
          </div>
        </div>

        {eco?.status === "error" && (
          <div className="mt-3 text-xs text-[#E24B4A] flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Échec de la projection : {eco.error}
          </div>
        )}
        {generating && (
          <div className="mt-3 text-xs text-zinc-400 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#378ADD]" />
            L'IA projette l'écosystème présumé de {entity.name} (banquier, fiduciaire, fournisseurs, clients de clients…) — ~30 s.
          </div>
        )}
        {!eco?.status || eco?.status === "none" ? (
          <p className="mt-3 text-xs text-zinc-500">
            Aucune projection encore. Lancez la projection : l'IA déduit les acteurs qui gravitent
            très probablement autour de ce client et les chemins d'introduction possibles.
          </p>
        ) : null}
      </div>

      {nodes.length > 0 && (
        <div className="noam-card p-4" data-testid="origination-eco-nodes">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
            Écosystème présumé — {nodes.length} acteurs (triés par score d'origination)
          </div>
          <div className="space-y-2">
            {nodes.map((n) => <NodeCard key={n.id} node={n} clientName={entity.name} clientId={entity.id} />)}
          </div>
        </div>
      )}

      <FrictionPanel entity={entity} />
    </div>
  );
}

function NodeCard({ node, clientName, clientId }) {
  const [open, setOpen] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const priority = node.origination_score >= 70 ? "haute" : node.origination_score >= 45 ? "moyenne" : "basse";

  const toLead = async (e) => {
    e.stopPropagation();
    setLeadBusy(true);
    try {
      const r = await api.post("/workspace/annuaire/origination/target/to-lead", {
        target_name: node.name, role: node.role, sector: node.sector || "",
        client_name: clientName, client_id: clientId,
        introduction_path: node.introduction_path || "", origination_score: node.origination_score,
      });
      setLeadDone(true);
      notify.success(r.data.already_exists ? "Lead déjà existant." : "Cible convertie en Lead.");
    } catch (err) { notify.error(fmtError(err)); }
    setLeadBusy(false);
  };

  return (
    <div
      className="border border-white/10 rounded-lg p-3 hover:border-[#378ADD]/30 transition-colors cursor-pointer"
      onClick={() => setOpen((o) => !o)}
      data-testid={`origination-node-${node.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{node.name}</span>
          <RolePill role={node.role} />
          {node.annuaire_entity_id && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1D9E75]/15 text-[#1D9E75]">Déjà dans l'Annuaire</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-zinc-500">confiance {node.confidence}%</span>
          <ScoreBadge score={node.origination_score} priority={priority} />
          <SplcButton
            entityId={node.annuaire_entity_id || undefined}
            entityName={node.name}
            testid={`origination-node-splc-${node.id}`}
          />
          <button
            type="button"
            onClick={toLead}
            disabled={leadBusy || leadDone}
            className="btn-secondary text-[10px] px-2 py-1"
            data-testid={`origination-node-tolead-${node.id}`}
          >
            {leadBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : leadDone ? <CheckCircle2 className="w-3 h-3 text-[#1D9E75]" /> : <ArrowRight className="w-3 h-3" />}
            Lead
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-white/5 text-xs text-zinc-300 space-y-1.5">
          {node.why && <div><span className="text-zinc-500">Pourquoi présumé : </span>{node.why}</div>}
          {node.introduction_path && (
            <div><span className="text-[#378ADD]">Chemin d'introduction : </span>{node.introduction_path}</div>
          )}
          {(node.score_reasons || []).length > 0 && (
            <ul className="list-disc pl-4 text-zinc-400">
              {node.score_reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ===== Scan frictions ===== */

function FrictionPanel({ entity }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchScan = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/annuaire/origination/friction-scan/${entity.id}`);
      setScan(r.data);
      if (r.data.status !== "generating" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) { /* silencieux */ }
  }, [entity.id]);

  useEffect(() => {
    fetchScan();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchScan]);

  useEffect(() => {
    if (scan?.status === "generating" && !pollRef.current) {
      pollRef.current = setInterval(fetchScan, 3000);
    }
  }, [scan?.status, fetchScan]);

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/workspace/annuaire/origination/friction-scan/${entity.id}`);
      setScan((prev) => ({ ...(prev || {}), status: "generating" }));
      if (!pollRef.current) pollRef.current = setInterval(fetchScan, 3000);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const generating = scan?.status === "generating";

  return (
    <div className="noam-card p-4" data-testid="origination-friction-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5">
            <Radar className="w-4 h-4 text-[#EF9F27]" /> Scan frictions — app + web
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Croise les signaux internes (deals, journal) et les actualités web pour détecter
            les problèmes probables de {entity.name} — et nos angles d'approche.
          </p>
        </div>
        <button
          type="button"
          onClick={start}
          disabled={busy || generating}
          className="btn-secondary text-xs"
          data-testid="origination-friction-scan-btn"
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />}
          {generating ? "Scan en cours…" : scan?.status === "done" ? "Re-scanner" : "Scanner les frictions"}
        </button>
      </div>
      {scan?.status === "error" && (
        <div className="mt-3 text-xs text-[#E24B4A]">Échec du scan : {scan.error}</div>
      )}
      {scan?.status === "done" && scan.frictions_md && (
        <div className="mt-3">
          <AiText>{scan.frictions_md}</AiText>
          {(scan.sources || []).length > 0 && (
            <div className="mt-3 pt-2 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Sources web</div>
              {scan.sources.map((s) => (
                <a key={s.link || s.title} href={s.link} target="_blank" rel="noreferrer" className="block text-[11px] text-[#378ADD] hover:underline truncate">
                  {s.title}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===== Vue 2 — Chemins d'introduction ===== */

function PathsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workspace/annuaire/origination/targets")
      .then((r) => setData(r.data))
      .catch((e) => notify.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;

  const targets = data?.targets || [];
  if (!targets.length) {
    return (
      <div className="noam-card p-8 text-center text-zinc-400 text-sm" data-testid="origination-paths-empty">
        Aucune cible encore — projetez d'abord au moins un écosystème (onglet « Écosystèmes présumés »).
      </div>
    );
  }

  return (
    <div className="noam-card p-0 overflow-x-auto" data-testid="origination-paths-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
            <th className="px-3 py-2">Acteur cible</th>
            <th className="px-3 py-2">Rôle</th>
            <th className="px-3 py-2">Introduit par</th>
            <th className="px-3 py-2">Chemin d'introduction</th>
            <th className="px-3 py-2">Priorité</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {targets.map((t) => <PathRow key={`${t.client_id}-${t.target_name}`} t={t} />)}
        </tbody>
      </table>
    </div>
  );
}

function PathRow({ t }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(t.already_lead);

  const toLead = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/annuaire/origination/target/to-lead", {
        target_name: t.target_name, role: t.role, sector: t.sector || "",
        client_name: t.client_name, client_id: t.client_id,
        introduction_path: t.introduction_path || "", origination_score: t.origination_score,
      });
      setDone(true);
      notify.success(r.data.already_exists ? "Lead déjà existant." : "Cible convertie en Lead.");
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]">
      <td className="px-3 py-2 font-medium">{t.target_name}</td>
      <td className="px-3 py-2"><RolePill role={t.role} /></td>
      <td className="px-3 py-2 text-[#378ADD]">{t.client_name}</td>
      <td className="px-3 py-2 text-xs text-zinc-400 max-w-[320px]">{t.introduction_path}</td>
      <td className="px-3 py-2"><ScoreBadge score={t.origination_score} priority={t.priority} /></td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={toLead}
          disabled={busy || done}
          className="btn-secondary text-[10px] px-2 py-1"
          data-testid={`origination-path-tolead-${t.target_name}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : done ? <CheckCircle2 className="w-3 h-3 text-[#1D9E75]" /> : <ArrowRight className="w-3 h-3" />}
          {done ? "Lead créé" : "Lead"}
        </button>
      </td>
    </tr>
  );
}

/* ===== Vue 3 — Matrice d'influence ===== */

function MatrixTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workspace/annuaire/origination/matrix")
      .then((r) => setData(r.data))
      .catch((e) => notify.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;

  const clients = data?.clients || [];
  const targets = data?.targets || [];
  if (!targets.length) {
    return (
      <div className="noam-card p-8 text-center text-zinc-400 text-sm" data-testid="origination-matrix-empty">
        Matrice vide — projetez d'abord au moins un écosystème.
      </div>
    );
  }

  const cellColor = (score) => {
    if (score == null) return "";
    if (score >= 70) return "bg-[#1D9E75]/25 text-[#1D9E75]";
    if (score >= 45) return "bg-[#EF9F27]/20 text-[#EF9F27]";
    return "bg-white/5 text-zinc-400";
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-zinc-500">
        Lignes = cibles d'origination · Colonnes = vos clients (canaux d'introduction).
        Une cible atteignable par plusieurs clients (multi-canal) gagne un bonus de score.
      </p>
      <div className="noam-card p-0 overflow-x-auto" data-testid="origination-matrix-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
              <th className="px-3 py-2 sticky left-0 bg-[#0d0f14]">Cible</th>
              <th className="px-3 py-2">Score global</th>
              <th className="px-3 py-2">Canaux</th>
              {clients.map((c) => (
                <th key={c.id} className="px-3 py-2 whitespace-nowrap max-w-[140px] truncate" title={c.name}>{c.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {targets.map((t) => (
              <tr key={t.name} className={`border-b border-white/5 hover:bg-white/[0.02] ${t.channels_count > 1 ? "bg-[#EF9F27]/[0.04]" : ""}`}>
                <td className="px-3 py-2 sticky left-0 bg-[#0d0f14]">
                  <div className="font-medium">{t.name}</div>
                  <div className="mt-0.5"><RolePill role={t.role} /></div>
                </td>
                <td className="px-3 py-2"><ScoreBadge score={t.aggregate_score} priority={t.priority} /></td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-semibold ${t.channels_count > 1 ? "text-[#EF9F27]" : "text-zinc-400"}`}>
                    {t.channels_count > 1 ? `×${t.channels_count} multi-canal` : "1 canal"}
                  </span>
                </td>
                {clients.map((c) => {
                  const s = t.channels?.[c.id];
                  return (
                    <td key={c.id} className="px-3 py-2">
                      {s != null ? (
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded font-semibold ${cellColor(s)}`}
                          title={t.paths?.[c.id] || ""}
                        >
                          {Math.round(s)}
                        </span>
                      ) : (
                        <span className="text-zinc-700">·</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
